import {ApiStatus, IRole} from '../../models';
import {RolesAction, RolesActionTypes} from '../actions/roleActions';
import {isUserDefinedRole} from '../../utils/auth';

export const initialRoleState: IRoleState = {
  loadingStatus: ApiStatus.LOADING,
  creatingStatus: null,
  updatingStatus: null,
  deletingStatus: null,
  roles: [],
  groups: [],
  canLoadMore: false,
}

export interface IRoleState {
  loadingStatus: ApiStatus;
  creatingStatus: ApiStatus | null;
  updatingStatus: ApiStatus | null;
  deletingStatus: ApiStatus | null;
  roles: IRole[];
  /*
   * Groups represent user defined roles. There are also "main_roles", which are BE defined.
   *
   * Groups don't have permissions, but resources, reports, etc. can be shared with a group.
   */
  groups: IRole[];
  canLoadMore: boolean;
}

export default function rolesReducer(state: IRoleState = initialRoleState, action: RolesAction): IRoleState {
  switch (action.type) {
    case RolesActionTypes.LOAD_ROLES:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADING
      };
    case RolesActionTypes.LOADING_ROLES_FAILED:
      return {
        ...state,
        loadingStatus: ApiStatus.FAILED
      };
    case RolesActionTypes.LOADED_ROLES:
      const newRoles = action.payload.roles;
      const reload = action.payload.reload || false;
      const page_size = action.payload.page_size || 0;

      return {
        ...state,
        loadingStatus: ApiStatus.LOADED,
        roles: action.payload.roles,
        groups: action.payload.roles.filter(isUserDefinedRole),
        canLoadMore: newRoles.length === page_size,
      };
    case RolesActionTypes.LOAD_ROLE:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADING,
      }
    case RolesActionTypes.LOADED_ROLE:
      return handleLoadedRole(state, action);
    case RolesActionTypes.LOADING_ROLE_FAILED:
      return {
        ...state,
        loadingStatus: ApiStatus.FAILED,
      }

    case RolesActionTypes.CREATE_ROLE:
      return {
        ...state,
        creatingStatus: ApiStatus.LOADING,
      }
    case RolesActionTypes.CREATED_ROLE:
      return {
        ...state,
        creatingStatus: ApiStatus.LOADED,
        roles: [
          ...state.roles || [],
          action.payload,
        ],
        groups: [
          ...state.groups || [],
          action.payload, // only groups can be created
        ]
      }
    case RolesActionTypes.CREATING_ROLE_FAILED:
      return {
        ...state,
        creatingStatus: ApiStatus.FAILED,
      }

    case RolesActionTypes.UPDATE_ROLE:
      return {
        ...state,
        updatingStatus: ApiStatus.LOADING,
      }
    case RolesActionTypes.UPDATED_ROLE:
      return {
        ...state,
        roles: state.roles ? state.roles.map((role: IRole) => {
          if (role.uid === action.payload.uid) {
            return {
              ...role,
              name: action.payload.name,
            }
          }
          return role;
        }) : state.roles,
        updatingStatus: ApiStatus.LOADED,
      }
    case RolesActionTypes.UPDATING_ROLE_FAILED:
      return {
        ...state,
        updatingStatus: ApiStatus.FAILED
      }

    case RolesActionTypes.DELETE_ROLE:
      return {
        ...state,
        deletingStatus: ApiStatus.LOADING,
      }
    case RolesActionTypes.DELETED_ROLE:
      return {
        ...state,
        roles: state.roles ? state.roles.filter((role: IRole) => role.uid !== action.payload) : state.roles,
        groups: state.groups ? state.groups.filter((g: IRole) => g.uid !== action.payload) : state.groups,
        deletingStatus: ApiStatus.LOADED,
      }
    case RolesActionTypes.DELETING_ROLE_FAILED:
      return {
        ...state,
        deletingStatus: ApiStatus.FAILED
      }

    case RolesActionTypes.ASSIGN_ROLE:
    case RolesActionTypes.REVOKE_ROLE:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADING,
      }
    case RolesActionTypes.ASSIGNED_ROLE:
    case RolesActionTypes.REVOKED_ROLE:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADED,
      }
    case RolesActionTypes.ASSIGNING_ROLE_FAILED:
    case RolesActionTypes.REVOKING_ROLE_FAILED:
      return {
        ...state,
        loadingStatus: ApiStatus.FAILED,
      }
    default:
      return state;
  }
}

function handleLoadedRole(state: IRoleState, action: any): IRoleState {
  const newRoles = state.roles ? state.roles.map((role: IRole) => {
    if (role.uid === action.payload.uid) {
      return action.payload;
    }

    return role;
  }) : [action.payload];

  return {
    ...state,
    roles: newRoles,
    groups: newRoles.filter(isUserDefinedRole),
    loadingStatus: ApiStatus.LOADED,
  }
}
